import { Avatar, Typography, Tag, Space } from 'antd'
import Button from '../../../../../core/components/Button.jsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faBriefcase, faHandshake, faUserTie, IconDefinition } from '@fortawesome/free-solid-svg-icons'

const { Text, Title } = Typography

interface TeamMemberDrawerProps {
  member: {
    id?: string
    name: string
    title?: string
    email?: string
    avatar?: string
    initials?: string
    role?: string
    dateAdded?: string | Date
  } | null
  onClose: () => void
}

export function TeamMemberDrawer({ member = null, onClose }: TeamMemberDrawerProps) {
  const availableRoles: Array<{
    value: string
    label: string
    icon: IconDefinition
    color: string
  }> = [
    { value: 'introduced_by', label: 'Introduced by', icon: faHandshake, color: 'blue' },
    { value: 'sourced_by', label: 'Sourced by', icon: faUser, color: 'green' },
    { value: 'lead', label: 'Deal Lead', icon: faUserTie, color: 'purple' },
    { value: 'analyst', label: 'Analyst', icon: faBriefcase, color: 'orange' },
    { value: 'advisor', label: 'Advisor', icon: faUser, color: 'cyan' },
    { value: 'board_observer', label: 'Board Observer', icon: faUserTie, color: 'magenta' },
    { value: 'sponsor', label: 'Sponsor', icon: faHandshake, color: 'gold' }
  ]

  const getRoleConfig = (roleValue: string) => {
    return (
      availableRoles.find((role) => role.value === roleValue) || {
        label: roleValue,
        icon: faUser,
        color: 'default'
      }
    )
  }

  if (!member) {
    return (
      <div data-debug='team-member-drawer-empty' className='p-6 text-center'>
        <Text type='secondary'>No team member selected</Text>
      </div>
    )
  }

  return (
    <div
      data-debug='team-member-drawer-container'
      data-component='TeamMemberDrawer'
      data-member-id={member?.id}
      data-member-role={member?.role}
      className='space-y-6'
    >
      <div data-debug='team-member-drawer-profile' className='text-center pb-4 border-b border-gray-200'>
        <div data-debug='team-member-drawer-avatar' className='mb-4'>
          {member.avatar ? (
            <Avatar size={80} src={member.avatar} />
          ) : (
            <Avatar size={80} className='bg-blue-500 text-white text-xl font-bold'>
              {member.initials}
            </Avatar>
          )}
        </div>
        <Title level={4} className='mb-1'>
          {member.name}
        </Title>
        <Text type='secondary' className='block text-sm'>
          {member.title}
        </Text>
      </div>

      <div data-debug='team-member-drawer-contact'>
        <Title level={5} className='mb-3'>
          Contact Information
        </Title>
        <Space direction='vertical' size='small' className='w-full'>
          <div className='flex justify-between'>
            <Text type='secondary'>Email:</Text>
            <Text copyable={{ text: member.email }}>{member.email}</Text>
          </div>
          <div className='flex justify-between'>
            <Text type='secondary'>Added to Deal:</Text>
            <Text>{member.dateAdded ? new Date(member.dateAdded).toLocaleDateString() : ''}</Text>
          </div>
        </Space>
      </div>

      <div data-debug='team-member-drawer-role'>
        <Title level={5} className='mb-3'>
          Role in Deal
        </Title>
        <div data-debug='team-member-drawer-role-badge' className='bg-gray-50 p-3 rounded-lg'>
          <Tag
            color={getRoleConfig(member.role || '').color}
            className='mb-2'
            icon={<FontAwesomeIcon icon={getRoleConfig(member.role || '').icon} />}
          >
            {getRoleConfig(member.role || '').label}
          </Tag>
          <Text type='secondary' className='block text-sm'>
            This team member's role and responsibilities in this deal.
          </Text>
        </div>
      </div>

      <div data-debug='team-member-drawer-actions' className='pt-4 border-t border-gray-200'>
        <Space className='w-full justify-center'>
          <Button variant='cancel' data-debug='team-member-drawer-close-btn' onClick={onClose}>
            Close
          </Button>
        </Space>
      </div>
    </div>
  )
}
